import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithCustomToken,
  ConfirmationResult,
} from 'firebase/auth';
import { useMutation, gql } from '@apollo/client';
import { auth } from '../config/firebase';
import logo from '../images/logo.png';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const REQUEST_LOGIN_MAGIC_LINK = gql`
  mutation RequestLoginMagicLink($email: String!) {
    requestLoginMagicLink(email: $email) {
      success
      message
      error
    }
  }
`;

// DEV ONLY: Bypass email verification for faster testing
const DEV_LOGIN = gql`
  mutation DevLogin($email: String!) {
    devLogin(email: $email) {
      valid
      firebaseToken
      email
      error
    }
  }
`;

type AuthMethod = 'phone' | 'email';

// Check if running in development mode (localhost)
const isDevelopment = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname === '0.0.0.0';

export default function AuthPage() {
  useDocumentTitle('Sign In');
  const navigate = useNavigate();

  // Check if user was redirected here due to session expiration
  const [sessionExpired] = useState(() => {
    return sessionStorage.getItem('redirectAfterLogin') !== null;
  });

  // Auth method selection
  const [authMethod, setAuthMethod] = useState<AuthMethod>('email');

  // Phone auth state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);

  // Email auth state
  const [email, setEmail] = useState(isDevelopment ? 'ahmed.kamel@gmail.com' : '');
  const [emailSent, setEmailSent] = useState(false);

  // Shared state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);

  // GraphQL mutation for email magic link
  const [requestLoginMagicLink] = useMutation(REQUEST_LOGIN_MAGIC_LINK);

  // DEV ONLY: GraphQL mutation for dev login bypass
  const [devLoginMutation] = useMutation(DEV_LOGIN);

  // Initialize reCAPTCHA
  const initializeRecaptcha = () => {
    // Always clear and recreate to avoid stale state
    if (recaptchaRef.current) {
      try {
        recaptchaRef.current.clear();
      } catch (e) {
        console.log('Could not clear previous reCAPTCHA');
      }
      recaptchaRef.current = null;
    }

    try {
      console.log('[DEBUG] Creating RecaptchaVerifier, auth.config:', {
        apiKey: auth.config.apiKey,
        authDomain: auth.config.authDomain,
        emulator: (auth as any).config?.emulatorHost
      });

      recaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA solved');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
          setError('reCAPTCHA expired. Please try again.');
        },
      });
      console.log('RecaptchaVerifier initialized successfully');
    } catch (error) {
      console.error('Error initializing RecaptchaVerifier:', error);
      throw error;
    }

    return recaptchaRef.current;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.clear();
        recaptchaRef.current = null;
      }
    };
  }, []);

  // Clear error when switching auth methods
  useEffect(() => {
    setError(null);
  }, [authMethod]);

  const handleSendSMS = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // For emulator, we can use test phone numbers that bypass reCAPTCHA
      // Check if we're in development mode
      const isDevelopment = window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1';

      if (isDevelopment) {
        console.log('Using emulator - attempting phone auth without reCAPTCHA verification');
      }

      const recaptcha = initializeRecaptcha();
      const result = await signInWithPhoneNumber(auth, phoneNumber, recaptcha);

      setConfirmationResult(result);
      setShowVerificationDialog(true);
      console.log('SMS sent successfully');
    } catch (error: any) {
      console.error('Error sending SMS:', error);

      // Check if we're in development mode
      const isDev = window.location.hostname === 'localhost' ||
                    window.location.hostname === '127.0.0.1';

      // Provide more helpful error message for network issues
      if (error.code === 'auth/network-request-failed' && isDev) {
        // Known issue: RecaptchaVerifier in Firebase JS SDK v11 has trouble with emulator
        // The emulator IS working (check terminal), but reCAPTCHA can't initialize properly
        setError('reCAPTCHA network error in emulator. This is a known Firebase SDK v11 issue. The emulator may still have generated a code - check the terminal running "make emulate" for verification codes.');
        console.error('[WORKAROUND NEEDED] RecaptchaVerifier cannot connect in emulator mode.');
        console.error('Check the Firebase emulator terminal for verification codes.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Make sure the Firebase emulator is running and accessible.');
      } else {
        setError(error.message || 'Failed to send SMS. Please check your phone number.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    if (!confirmationResult) {
      setError('No confirmation result. Please request a new code.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await confirmationResult.confirm(verificationCode);
      console.log('Phone authentication successful');

      // Close dialog and navigate
      setShowVerificationDialog(false);
      setVerificationCode('');
      setPhoneNumber('');

      // Check if there's a redirect path stored (from auth error redirect)
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      } else {
        navigate('/');
      }
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setError(error.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMagicLink = async () => {
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data } = await requestLoginMagicLink({
        variables: { email: email.trim() },
      });

      if (data.requestLoginMagicLink.success) {
        setEmailSent(true);
      } else {
        setError(data.requestLoginMagicLink.error || 'Failed to send magic link');
      }
    } catch (error: any) {
      console.error('Error sending magic link:', error);
      setError(error.message || 'Failed to send magic link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // DEV ONLY: Direct sign-in without email verification
  const handleDevLogin = async () => {
    if (!isDevelopment) {
      setError('Dev login is only available in development mode');
      return;
    }

    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data } = await devLoginMutation({
        variables: { email: email.trim() },
      });

      if (!data.devLogin.valid) {
        setError(data.devLogin.error || 'Dev login failed');
        return;
      }

      // Sign in with the custom token
      const firebaseToken = data.devLogin.firebaseToken;
      if (!firebaseToken) {
        setError('No authentication token received');
        return;
      }

      await signInWithCustomToken(auth, firebaseToken);
      console.log('[DEV] Dev login successful');

      // Check if there's a redirect path stored
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      } else {
        navigate('/');
      }
    } catch (error: any) {
      console.error('[DEV] Dev login error:', error);
      setError(error.message || 'Dev login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseVerificationDialog = () => {
    setShowVerificationDialog(false);
    setError(null);
    setVerificationCode('');
  };

  const handleResetEmailState = () => {
    setEmailSent(false);
    setEmail('');
    setError(null);
  };

  // Email sent confirmation view
  if (emailSent) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '60px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Check Your Email
              </Typography>
            </Box>

            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
              We sent a sign-in link to:
            </Typography>

            <Typography variant="body1" sx={{ textAlign: 'center', fontWeight: 600, mb: 3 }}>
              {email}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 4 }}>
              Click the link in your email to sign in. The link expires in 7 days.
            </Typography>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleResetEmailState}
                sx={{ mr: 2 }}
              >
                Use Different Email
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setError(null);
                  handleSendMagicLink();
                }}
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Resend Email'}
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {/* Hidden reCAPTCHA container */}
      <div id="recaptcha-container"></div>

      <Card>
        <CardContent sx={{ py: 4 }}>
          {/* Header with Logo */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src={logo}
              alt="Planapus Logo"
              style={{ height: '60px', width: 'auto', marginBottom: '16px' }}
            />
            <Typography variant="h4" gutterBottom>
              Welcome to Planapus
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Sign in to get started
            </Typography>
          </Box>

          {/* Session Expired Alert */}
          {sessionExpired && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Your session has expired. Please sign in again to continue where you left off.
            </Alert>
          )}

          {/* Auth Method Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={authMethod}
              onChange={(_, value) => setAuthMethod(value)}
              centered
            >
              <Tab
                value="email"
                label="Email"
                icon={<EmailIcon />}
                iconPosition="start"
              />
              <Tab
                value="phone"
                label="Phone"
                icon={<PhoneIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Email Auth */}
          {authMethod === 'email' && (
            <Box sx={{ mb: 3 }}>
              <TextField
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                fullWidth
                sx={{ mb: 2 }}
                disabled={isLoading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSendMagicLink();
                  }
                }}
              />
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSendMagicLink}
                disabled={isLoading || !email.trim()}
                startIcon={isLoading ? <CircularProgress size={20} /> : <EmailIcon />}
                endIcon={!isLoading ? <ArrowIcon /> : undefined}
              >
                {isLoading ? 'Sending...' : 'Send Sign-In Link'}
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'center', mt: 2 }}
              >
                We'll send you a link to sign in - no password needed
              </Typography>

              {/* DEV ONLY: Quick sign-in button for faster testing */}
              {isDevelopment && (
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={handleDevLogin}
                    disabled={isLoading || !email.trim()}
                    color="warning"
                    sx={{ mb: 1 }}
                  >
                    {isLoading ? 'Signing in...' : 'Dev: Sign In Directly'}
                  </Button>
                  <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ display: 'block', textAlign: 'center' }}
                  >
                    DEV ONLY - bypasses email verification
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Phone Auth */}
          {authMethod === 'phone' && (
            <Box sx={{ mb: 3 }}>
              <MuiTelInput
                label="Phone Number"
                value={phoneNumber}
                onChange={(value) => setPhoneNumber(value)}
                defaultCountry="US"
                fullWidth
                sx={{ mb: 2 }}
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSendSMS();
                  }
                }}
              />
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSendSMS}
                disabled={isLoading || !phoneNumber.trim()}
                startIcon={isLoading ? <CircularProgress size={20} /> : <PhoneIcon />}
                endIcon={!isLoading ? <ArrowIcon /> : undefined}
              >
                {isLoading ? 'Sending SMS...' : 'Send Verification Code'}
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'center', mt: 2 }}
              >
                We'll send you a verification code to confirm your phone number
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Verification Code Dialog */}
      <Dialog
        open={showVerificationDialog}
        onClose={handleCloseVerificationDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Enter Verification Code</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We sent a verification code to {phoneNumber}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            fullWidth
            placeholder="123456"
            disabled={isLoading}
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleVerifyCode();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVerificationDialog} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleVerifyCode}
            disabled={isLoading || !verificationCode.trim()}
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
